import type { Mention } from "@repo/shared-types";

/**
 * Mentions in a comment (7e-2).
 *
 * The api keeps each mention as a token of the person's id — "<@", the id,
 * ">" — so a mention follows its person through a rename; anything somebody
 * typed that looked like one arrives escaped, "<@" as "<@@". A comment's body
 * is read back into text and mentions here, and each mention shows the
 * username the api sent with it now, or "deleted account".
 *
 * What goes the other way is plain: people type "@username", and the api
 * resolves it against the space's members. An edit starts from the comment's
 * `text`, which is exactly that.
 */

export type BodyPart = { text: string } | { mention: Mention };

const TOKEN = /^<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/;

/** A stored body as text and mentions, in order. */
export function splitBody(body: string, mentions: Mention[]): BodyPart[] {
  const byId = new Map(mentions.map((mention) => [mention.id, mention]));
  const parts: BodyPart[] = [];
  let text = "";
  for (let i = 0; i < body.length; ) {
    if (body.startsWith("<@@", i)) {
      text += "<@";
      i += 3;
      continue;
    }
    const token = body.startsWith("<@", i) ? TOKEN.exec(body.slice(i)) : null;
    if (token) {
      if (text) parts.push({ text });
      text = "";
      const id = token[1]!;
      parts.push({ mention: byId.get(id) ?? { id, username: null } });
      i += token[0].length;
      continue;
    }
    text += body[i];
    i += 1;
  }
  if (text) parts.push({ text });
  return parts;
}

/**
 * The @username being typed where the caret is, if any: where its "@" is and
 * what follows it so far. A mention starts a word, as the api reads it.
 */
export function mentionAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|[^A-Za-z0-9_@-])@([A-Za-z0-9_-]{0,30})$/.exec(before);
  if (!match) return null;
  const query = match[2]!;
  return { start: caret - query.length - 1, query: query.toLowerCase() };
}

/**
 * The text with the mention being typed replaced by a whole one, and a space
 * after it; and where the caret goes next.
 */
export function insertMention(
  text: string,
  at: { start: number; query: string },
  username: string,
): { text: string; caret: number } {
  const end = at.start + 1 + at.query.length;
  const mention = `@${username} `;
  const rest = text.slice(end).replace(/^ /, "");
  return { text: text.slice(0, at.start) + mention + rest, caret: at.start + mention.length };
}

/** The six reactions, in the order a bar shows them, each with what it looks like. */
export const REACTIONS: { name: string; emoji: string; label: string }[] = [
  { name: "fire", emoji: "🔥", label: "Fire" },
  { name: "muscle", emoji: "💪", label: "Strong" },
  { name: "clap", emoji: "👏", label: "Clap" },
  { name: "heart", emoji: "❤️", label: "Love" },
  { name: "laugh", emoji: "😂", label: "Laugh" },
  { name: "wow", emoji: "😮", label: "Wow" },
];

/** How a reaction shows: its emoji, or its name for one this client does not know. */
export function reactionEmoji(name: string): string {
  return REACTIONS.find((reaction) => reaction.name === name)?.emoji ?? name;
}
